import type { NectarPageBlock } from '~/utils/nectarPageBlock';

export type CommunityPushOptInSurface =
  | typeof NectarPageBlock.CommunityAppPushPrompt
  | typeof NectarPageBlock.CommunityAppSettings;

export type CommunityPushSurface = CommunityPushOptInSurface | typeof NectarPageBlock.CommunityAppLogout;

export type CommunityPushContext = {
  storageKey: string;
  trackingEnabled?: boolean;
  csrfToken: string;
  pageBlock: CommunityPushSurface;
};

export const adoptionStorageKey = (publicationId: string, memberId: string) =>
  `communityAppPush:${publicationId}:${memberId}`;

const acknowledgedHashes = new Map<string, string | null>();
export const COMMUNITY_PUSH_CHANGED = 'community-push-changed';

export const readAcknowledgedHash = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return acknowledgedHashes.get(key) ?? null;
  }
};

export const acknowledgeHash = (key: string, hash: string | null) => {
  acknowledgedHashes.set(key, hash);
  try {
    if (hash) localStorage.setItem(key, hash);
    else localStorage.removeItem(key);
  } catch {
    // Keep this visit usable when browser storage is unavailable.
  }
  window.dispatchEvent(new Event(COMMUNITY_PUSH_CHANGED));
};

export const hasCommunityManifest = () =>
  document.querySelector('link[rel="manifest"]')?.getAttribute('href') === '/community/manifest.webmanifest';

export const isStandaloneCommunity = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  ('standalone' in navigator && navigator.standalone === true);

export const isCommunityPushPath = () =>
  window.location.pathname === '/community' || window.location.pathname.startsWith('/community/');

export const getCommunityPushRegistration = async () => {
  const registration = await navigator.serviceWorker.getRegistration('/community');
  if (!registration || new URL(registration.scope).pathname.replace(/\/$/, '') !== '/community') return null;
  return registration;
};

let activeCommunityPushContext: CommunityPushContext | undefined;
export const setCommunityPushContext = (context: CommunityPushContext | undefined) => {
  activeCommunityPushContext = context;
  window.dispatchEvent(new Event(COMMUNITY_PUSH_CHANGED));
};
export const getCommunityPushContext = () => activeCommunityPushContext;

let pushOperation: Promise<unknown> = Promise.resolve();
export const withCommunityPushLock = <T>(operation: () => Promise<T>): Promise<T> => {
  const next = pushOperation.then(operation, operation);
  pushOperation = next.catch(() => undefined);
  return next;
};

export const isAcknowledgedCommunityPush = async (subscription: PushSubscription): Promise<boolean> => {
  const context = getCommunityPushContext();
  if (!context?.trackingEnabled) return true;
  const bytes = new TextEncoder().encode(JSON.stringify(subscription.toJSON()));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const hash = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  return readAcknowledgedHash(context.storageKey) === hash;
};

export const fetchPushRequest = async (path: string, options: RequestInit): Promise<Response> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10_000);
  try {
    return await fetch(path, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
};
